/**
 * Photo Gallery Component
 * Displays and manages user's profile photos
 */

import React, { useState, useRef } from 'react';
import { 
  Image, 
  Upload, 
  Trash2, 
  Camera, 
  X, 
  ArrowLeft, 
  ArrowRight,
  Loader,
  AlertCircle
} from 'lucide-react';
import { uploadProfilePhoto, deleteProfilePhoto } from '../services/profilePhotoService';

/**
 * Photo Gallery Component
 * 
 * @param {Object} props - Component props
 * @param {Array} props.photos - Array of photo URLs
 * @param {boolean} props.isEditing - Whether the component is in edit mode
 * @param {Function} props.onChange - Function to handle photo changes
 * @param {string} props.userId - User ID
 * @returns {JSX.Element} Photo gallery component
 */
const PhotoGallery = ({ photos = [], isEditing, onChange, userId }) => {
  const fileInputRef = useRef(null);
  const [currentPhoto, setCurrentPhoto] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState(null);
  const [previewPhoto, setPreviewPhoto] = useState(null);
  
  const MAX_PHOTOS = 6;
  
  // Handle file input change
  const handleFileInputChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    // Validate file type
    const validTypes = ['image/jpeg', 'image/png', 'image/jpg'];
    if (!validTypes.includes(file.type)) {
      setError('Please select a JPG or PNG image');
      return;
    }
    
    // Validate file size (5MB max)
    if (file.size > 5 * 1024 * 1024) {
      setError('Image must be less than 5MB');
      return;
    }
    
    // Create preview
    const reader = new FileReader();
    reader.onload = () => {
      setPreviewPhoto(reader.result);
    };
    reader.readAsDataURL(file);
    
    // Upload photo
    setUploading(true);
    setError(null);
    try {
      const photoUrl = await uploadProfilePhoto(userId, file);
      const updatedPhotos = [...photos, photoUrl];
      onChange(updatedPhotos);
      setCurrentPhoto(updatedPhotos.length - 1);
    } catch (err) {
      console.error('Error uploading photo:', err);
      setError('Failed to upload photo. Please try again.');
    } finally {
      setUploading(false);
      setPreviewPhoto(null);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };
  
  // Handle delete photo
  const handleDeletePhoto = async (index) => {
    if (deleting) return;
    
    const photoToDelete = photos[index];
    setDeleting(true);
    setError(null);
    try {
      await deleteProfilePhoto(userId, photoToDelete);
      const updatedPhotos = photos.filter((_, i) => i !== index);
      onChange(updatedPhotos);
      if (currentPhoto >= updatedPhotos.length) {
        setCurrentPhoto(Math.max(0, updatedPhotos.length - 1));
      }
    } catch (err) {
      console.error('Error deleting photo:', err);
      setError('Failed to delete photo. Please try again.');
    } finally {
      setDeleting(false);
    }
  };
  
  // Handle previous photo
  const handlePreviousPhoto = () => {
    setCurrentPhoto((prev) => (prev > 0 ? prev - 1 : photos.length - 1));
  };
  
  // Handle next photo
  const handleNextPhoto = () => {
    setCurrentPhoto((prev) => (prev < photos.length - 1 ? prev + 1 : 0));
  };
  
  // Trigger file input click
  const handleUploadClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };
  
  // Render empty state when no photos
  if (photos.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center p-8 border-2 border-dashed border-gray-300 rounded-xl bg-gray-50">
        <Image className="w-12 h-12 text-gray-400 mb-4" />
        <h3 className="text-lg font-medium text-gray-900 mb-2">No photos yet</h3>
        <p className="text-sm text-gray-500 text-center mb-6">
          Add photos to your profile to help others get to know you better
        </p>
        
        {isEditing && (
          <div>
            <input
              type="file"
              ref={fileInputRef}
              className="hidden"
              accept="image/jpeg,image/png"
              onChange={handleFileInputChange}
            />
            <button
              type="button"
              onClick={handleUploadClick}
              disabled={uploading}
              className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
            >
              {uploading ? (
                <>
                  <Loader className="animate-spin -ml-1 mr-2 h-5 w-5 text-white" />
                  Uploading...
                </>
              ) : (
                <>
                  <Upload className="-ml-1 mr-2 h-5 w-5" />
                  Upload Photo
                </>
              )}
            </button>
          </div>
        )}
        
        {error && (
          <div className="mt-4 text-sm text-red-600 flex items-center">
            <AlertCircle className="h-4 w-4 mr-1" />
            {error}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Photo Viewer */}
      <div className="relative">
        <div className="aspect-w-4 aspect-h-3 rounded-lg overflow-hidden bg-gray-100">
          {previewPhoto ? (
            <img
              src={previewPhoto}
              alt="Preview"
              className="w-full h-full object-cover"
            />
          ) : (
            <img
              src={photos[currentPhoto]}
              alt={`Photo ${currentPhoto + 1}`}
              className="w-full h-full object-cover"
            />
          )}
          
          {/* Loading Overlay */}
          {(uploading || deleting) && (
            <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center">
              <Loader className="h-8 w-8 text-white animate-spin" />
            </div>
          )}
        </div>
        
        {/* Navigation Controls (only if more than 1 photo) */}
        {photos.length > 1 && (
          <div className="absolute inset-0 flex items-center justify-between">
            <button
              type="button"
              onClick={handlePreviousPhoto}
              className="p-1 rounded-full bg-white bg-opacity-75 text-gray-800 hover:bg-opacity-100 focus:outline-none ml-2"
            >
              <ArrowLeft className="h-5 w-5" />
            </button>
            <button
              type="button"
              onClick={handleNextPhoto}
              className="p-1 rounded-full bg-white bg-opacity-75 text-gray-800 hover:bg-opacity-100 focus:outline-none mr-2"
            >
              <ArrowRight className="h-5 w-5" />
            </button>
          </div>
        )}
        
        {/* Delete Button (in edit mode) */}
        {isEditing && photos.length > 0 && (
          <button
            type="button"
            onClick={() => handleDeletePhoto(currentPhoto)}
            disabled={deleting}
            className="absolute top-2 right-2 p-1.5 rounded-full bg-red-500 text-white hover:bg-red-600 focus:outline-none"
          >
            <Trash2 className="h-5 w-5" />
          </button>
        )}
        
        {/* Photo Counter */}
        <div className="absolute bottom-2 right-2 px-2 py-1 rounded-full bg-black bg-opacity-50 text-white text-xs">
          {currentPhoto + 1} / {photos.length}
        </div>
      </div>
      
      {/* Thumbnails */}
      <div className="grid grid-cols-6 gap-2">
        {photos.map((photo, index) => (
          <button
            key={index}
            type="button"
            onClick={() => setCurrentPhoto(index)}
            className={`aspect-square rounded overflow-hidden ${
              index === currentPhoto ? 'ring-2 ring-green-500' : ''
            }`}
          >
            <img
              src={photo}
              alt={`Thumbnail ${index + 1}`}
              className="w-full h-full object-cover"
            />
          </button>
        ))}
        
        {/* Upload Button (in edit mode) */}
        {isEditing && photos.length < MAX_PHOTOS && (
          <button
            type="button"
            onClick={handleUploadClick}
            disabled={uploading}
            className="aspect-square rounded flex items-center justify-center border-2 border-dashed border-gray-300 hover:border-gray-400 bg-gray-50"
          >
            {uploading ? (
              <Loader className="h-6 w-6 text-gray-400 animate-spin" />
            ) : (
              <Camera className="h-6 w-6 text-gray-400" />
            )}
            <input
              type="file"
              ref={fileInputRef}
              className="hidden"
              accept="image/jpeg,image/png"
              onChange={handleFileInputChange}
            />
          </button>
        )}
      </div>
      
      {/* Error Message */}
      {error && (
        <div className="text-sm text-red-600 flex items-center">
          <AlertCircle className="h-4 w-4 mr-1" />
          {error}
        </div>
      )}
      
      {/* Tips */}
      {isEditing && (
        <div className="bg-blue-50 p-4 rounded-lg">
          <h4 className="text-sm font-medium text-blue-800 mb-2">Photo Tips</h4>
          <ul className="text-xs text-blue-700 space-y-1">
            <li>• Add clear photos of yourself to increase your profile visibility</li>
            <li>• First photo will be your main profile picture</li>
            <li>• You can add up to {MAX_PHOTOS} photos</li>
            <li>• Photos must be in JPG or PNG format and less than 5MB</li>
          </ul>
        </div>
      )}
    </div>
  );
};

export default PhotoGallery;